import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Shield, AlertTriangle, LogOut, X } from 'lucide-react';

const DISCLAIMER_KEY = 'gf_disclaimer_v1_accepted';

const LegalDisclaimer = () => {
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [hasSession, setHasSession] = useState(false);

  useEffect(() => {
    // Mostrar solo si el usuario no lo ha aceptado en esta sesión / dispositivo
    const accepted = localStorage.getItem(DISCLAIMER_KEY);
    if (!accepted) setShowDisclaimer(true);
    const token = localStorage.getItem('token');
    setHasSession(!!token);
  }, []);

  const handleAccept = () => {
    localStorage.setItem(DISCLAIMER_KEY, new Date().toISOString());
    setShowDisclaimer(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem(DISCLAIMER_KEY);
    setShowDisclaimer(false);
    window.location.href = '/';
  };

  return (
    <Dialog open={showDisclaimer} onOpenChange={() => {}}>
      <DialogContent 
        className="bg-zinc-900 border-zinc-700 max-w-lg p-0 overflow-hidden flex flex-col max-h-[90vh]"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Aviso legal de GuardiansFit</DialogTitle>
        <DialogDescription className="sr-only">
          Aviso de fase de desarrollo y derechos de propiedad intelectual de GUARDIANSFIT.
        </DialogDescription>
        {/* Close button in corner */}
        <button
          onClick={handleAccept}
          className="absolute right-4 top-4 text-gray-400 hover:text-white transition-colors z-10"
          aria-label="Cerrar"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header with brand */}
        <div className="bg-gradient-to-r from-primary/20 to-primary/5 p-6 border-b border-zinc-700 flex-shrink-0">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Shield className="h-8 w-8 text-primary" />
            <h1 className="text-2xl font-bold text-primary tracking-wide">
              GUARDIANSFIT<sup className="text-xs">®</sup>
            </h1>
          </div>
        </div>

        {/* Content (scrollable on mobile) */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Development notice */}
          <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-200">
              Esta aplicación se encuentra en <strong>fase de desarrollo y pruebas funcionales</strong>.
            </p>
          </div>

          {/* Legal text */}
          <div className="space-y-3 text-sm text-gray-300 leading-relaxed">
            <p>
              La marca <strong className="text-primary">GUARDIANSFIT®</strong>, así como sus contenidos, 
              metodología, estructura y materiales asociados, son titularidad de su creador y se 
              encuentran protegidos por la legislación vigente en materia de <strong>propiedad 
              intelectual y derechos de autor</strong>.
            </p>
            
            <p>
              El uso de esta versión no implica que se trate de un producto final ni comercial, 
              y está sujeta a <strong>modificaciones, mejoras y ajustes continuos</strong> como 
              parte del proceso de desarrollo.
            </p>
          </div>

          {/* Copyright footer */}
          <div className="pt-2 border-t border-zinc-700">
            <p className="text-xs text-center text-muted-foreground">
              © {new Date().getFullYear()} GUARDIANSFIT® - Todos los derechos reservados
            </p>
          </div>
        </div>

        {/* Action buttons (sticky bottom on mobile) */}
        <div className="p-4 bg-zinc-800/50 border-t border-zinc-700 space-y-2 flex-shrink-0">
          <Button 
            onClick={handleAccept}
            className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
            data-testid="disclaimer-accept-button"
          >
            Entendido
          </Button>
          
          {/* Logout button - always show, will work if there's a session */}
          {hasSession && (
            <Button 
              onClick={handleLogout}
              variant="ghost"
              className="w-full h-10 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
              data-testid="disclaimer-logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Cerrar sesión y cambiar de cuenta
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LegalDisclaimer;
